import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SERVICES, CATEGORIES } from "@/data/services";

const TABS = ["All", ...CATEGORIES];

export default function ServicesPage() {
  const [tab, setTab] = useState("All");
  const filtered = tab === "All" ? SERVICES : SERVICES.filter((s) => s.category === tab);

  return (
    <>
      <Seo
        title="Services — SAIVU | 15 practices: registration, tax, compliance & advisory"
        description="Business registration, DPIIT startup services, GST, income tax, accounting, payroll, virtual CFO, ROC, trademark, IEC, FSSAI, ISO, legal advisory, virtual office and business consulting — one team, fixed fees."
        path="/services"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: SERVICES.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: s.title,
            url: `https://saivu.in/services/${s.slug}`,
          })),
        }}
      />

      <section className="bg-mist pt-28 pb-14 sm:pt-32" data-testid="services-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-gold-deep sm:text-sm">
              The full index
            </p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.15] tracking-tight text-navy-900 sm:text-5xl">
              Everything your business must file, pay or prove.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink sm:text-lg">
              Fifteen services, one consulting company. Every engagement runs on fixed fees
              and a 50-50 payment structure — you only pay the balance after delivery.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="mt-8">
            <div className="flex flex-wrap gap-2.5" data-testid="service-filter-tabs">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  data-testid={`filter-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`rounded-md border px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                    tab === t
                      ? "border-navy-900 bg-navy-900 text-white"
                      : "border-line bg-white text-ink hover:border-navy-900 hover:text-navy-900"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              {filtered.map((s, i) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`service-row-${s.slug}`}
                  className="group grid grid-cols-12 items-center gap-4 border-b border-line px-2 py-7 transition-colors last:border-t hover:bg-mist sm:px-4"
                >
                  <span className="col-span-2 font-mono text-sm font-medium text-slate-600 sm:col-span-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="col-span-9 sm:col-span-7">
                    <span className="block text-lg font-semibold text-navy-900 sm:text-2xl">
                      {s.title}
                    </span>
                    <span className="mt-1 block text-sm text-ink sm:text-[15px]">{s.tagline}</span>
                  </span>
                  <span className="hidden sm:col-span-3 sm:block">
                    <span className="inline-block rounded-md border border-line bg-mist px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-slate-600">
                      {s.category}
                    </span>
                  </span>
                  <span className="col-span-1 flex justify-end">
                    <ArrowUpRight className="h-5 w-5 text-slate-600 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-navy-900" />
                  </span>
                </Link>
              ))}
            </motion.div>
          </AnimatePresence>

          <Reveal className="mt-12">
            <div className="rounded-lg border border-line bg-mist p-6 sm:p-7" data-testid="services-page-fee-disclaimer">
              <p className="text-base font-semibold text-navy-900">Fee Disclaimer</p>
              <p className="mt-2 text-[15px] leading-relaxed text-ink">
                The fees mentioned for our services are indicative range references only. The
                final professional fee/quotation may vary depending on the nature, scope and
                specific requirements of the engagement.{" "}
                <strong className="font-semibold text-navy-900">
                  Please consult or speak with our office to confirm the final fee/quotation
                  before making any payment.
                </strong>
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
